import b64toBlob from 'b64-to-blob'
import { useTranslation } from 'react-i18next'
import React, { useContext } from 'react'
import { AccountContext } from '@/contexts/account'
import { getSelfCheckinUploadKey, getUploadKey } from '@/apis/aipass'
import axios, { AxiosError } from 'axios'
import { reportError } from '@/components/apps/error-boundary'
import uuid from 'react-uuid'

export const useFileUpload = () => {
  const { t } = useTranslation()
  const { account } = useContext<any>(AccountContext)

  const inputFileHandler = (
    feature: string,
    isPublic: boolean,
    event: React.ChangeEvent<HTMLInputElement>,
    allowMineType: string[],
    onFileUploaded: (result: { uploadedPath: string; file: File }) => void,
  ) => {
    const files = event.target.files
    if (!files?.length) {
      return
    }
    const file = files[0]
    if (!allowMineType.includes(file?.type)) {
      window.alert(t('UnsupportedFileFormats'))
      return
    }
    const allowMaxFileByte = 10 * 1024 * 1024
    if (file.size > allowMaxFileByte) {
      window.alert(t('FileSizeTooLarge'))
      return
    }

    const reader = new FileReader()
    reader.onload = async e => {
      const fileData = e.target ? e.target.result : null
      if (fileData) {
        const uploadedPath = await upload({
          hotelId: account.employee.hotelId,
          file,
          feature,
          isPublic,
          fileData: fileData.toString(),
        })

        onFileUploaded({ uploadedPath: uploadedPath.url, file })
      }
    }
    reader.readAsDataURL(file)
  }

  const upload = async ({
    hotelId,
    file,
    fileData,
    feature,
    isPublic = false,
    selfCheckin,
  }: {
    hotelId: string
    file: { name: string; type: string }
    fileData: string | Blob
    feature: string
    isPublic?: boolean
    // セルフCI/CO 端末は未ログインでも動くため、スタッフ用ではなく予約で判定される署名を使う
    selfCheckin?: { reservationUuid?: string }
  }): Promise<{ url: string; objectKey: string }> => {
    let fileDataBlob
    if (typeof fileData === 'string') {
      const encodeTrimmedFileData = fileData.replace(`data:${file.type};base64,`, '')
      fileDataBlob = b64toBlob(encodeTrimmedFileData, file.type)
    } else {
      fileDataBlob = fileData
    }
    return await uploadS3({
      hotelId,
      feature,
      fileName: file.name,
      isPublic,
      contentType: file.type,
      file: fileDataBlob,
      selfCheckin,
    })
  }

  async function uploadS3(params: {
    hotelId: string
    feature: string
    fileName: string
    isPublic: boolean
    contentType: string
    file: Blob
    selfCheckin?: { reservationUuid?: string }
  }) {
    const uploadKey = params.selfCheckin
      ? await getSelfCheckinUploadKey({
          feature: params.feature,
          hotelId: params.hotelId,
          reservationUuid: params.selfCheckin.reservationUuid,
        })
      : await getUploadKey({
          isPublic: params.isPublic,
          feature: params.feature,
          hotelId: params.hotelId,
        })
    const form = new FormData()
    const objectKey = `${uploadKey.allowPath}/${appendUuidToFilename(params.fileName)}`
    form.append('key', objectKey)
    form.append('policy', uploadKey.policy)
    form.append('X-Amz-Credential', uploadKey.credential)
    form.append('X-Amz-Algorithm', 'AWS4-HMAC-SHA256')
    form.append('X-Amz-Date', uploadKey.signedDate)
    form.append('X-Amz-Signature', uploadKey.signature)
    form.append('Content-Type', params.contentType)
    form.append('file', params.file)
    await axios.post(uploadKey.s3url, form).catch((e: AxiosError) => {
      if (e.isAxiosError) {
        reportError(e.response?.data)
      } else {
        reportError(e)
      }
      alert('ファイルのアップロードに失敗しました')
      throw e
    })
    return {
      url: `${uploadKey.publicUrl}${objectKey}`,
      objectKey: objectKey,
    }
  }

  function appendUuidToFilename(filename: string): string {
    const filenameSuffix = uuid()
    const dotIndex = filename.lastIndexOf('.')
    if (dotIndex === -1) {
      return `${filename}_${filenameSuffix}`
    }
    const name = filename.slice(0, dotIndex)
    const ext = filename.slice(dotIndex)
    return `${name}_${filenameSuffix}${ext}`
  }

  return { inputFileHandler, upload }
}
